"use client";

import Link from "next/link";
import { ArrowRight, Smartphone, Globe, Database, Cpu, Briefcase, ShieldCheck, Lock, Code2 } from "lucide-react";
import ServicesGrid from "@/components/ServicesGrid";

export default function SoftwarePage() {
  return (
    <div className="min-h-screen bg-slate-50 pt-24">
      
      {/* --- HERO HEADER --- */}
      <div className="border-b border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-4 pt-10 pb-16 md:pt-16 md:pb-16">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            
            {/* LEFT COLUMN: TEXT */}
            <div className="text-left">
              <h1 className="text-4xl md:text-6xl font-bold font-sans text-slate-900 mb-6 flex items-center justify-start gap-0 tracking-tight">
                Software Solutions
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" className="text-red-600 h-[1em] w-[1.15em] -ml-5">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3.5" d="M9 5 l9 7 -9 7" />
                </svg>
              </h1>
              <p className="text-xl text-slate-600 mb-8 max-w-xl">
                From SaaS platforms and mobile apps to fractional CTO leadership. We build the technology that powers your business.
              </p>
              <div className="flex flex-wrap justify-start gap-4">
                <Link href="/contact-us" className="inline-flex items-center justify-center px-8 py-4 text-base font-bold text-white transition-all duration-200 bg-red-600 rounded-full hover:bg-red-700 hover:shadow-lg hover:-translate-y-1">
                  Start a Project
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </div>
            </div>

            {/* RIGHT COLUMN: IMAGE 
               UPDATED: Added max-h-80 w-auto.
            */}
            <div className="flex justify-center lg:justify-end">
              <img 
                src="https://placehold.co/800x600/ffffff/cbd5e1?text=Software+Dev+Visual" 
                alt="Software Development Visualization"
                className="w-auto max-h-80 object-contain shadow-sm rounded-xl"
              />
            </div>

          </div>
        </div>
      </div>

      {/* ... (Rest of page code is unchanged from previous turn) ... */}
      {/* --- SECTION 1: ENGINEERING (BUILD) --- */}
      <div className="max-w-7xl mx-auto px-4 pt-8 pb-16 md:pt-10 md:pb-24">
            <div className="text-center mb-12">
            <span className="text-red-600 font-bold tracking-wider uppercase text-sm">Build</span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mt-2">Full-Cycle Development</h2>
            <p className="text-slate-600 max-w-2xl mx-auto mt-4">
                Whether you need a consumer-facing mobile app or an enterprise SaaS platform, we ship clean, scalable code.
            </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="bg-white p-6 rounded-2xl border border-slate-200 hover:border-red-100 hover:shadow-lg transition-all group">
                    <Smartphone className="h-8 w-8 text-red-600 mb-4 group-hover:scale-110 transition-transform" />
                    <h3 className="text-lg font-bold text-slate-900 mb-2">Mobile App Dev</h3>
                    <p className="text-slate-600 text-sm">Native iOS and Android, or cross-platform React Native.</p>
                </div>
                <div className="bg-white p-6 rounded-2xl border border-slate-200 hover:border-red-100 hover:shadow-lg transition-all group">
                    <Globe className="h-8 w-8 text-red-600 mb-4 group-hover:scale-110 transition-transform" />
                    <h3 className="text-lg font-bold text-slate-900 mb-2">SaaS & Web Apps</h3>
                    <p className="text-slate-600 text-sm">Scalable, multi-tenant SaaS architectures.</p>
                </div>
                <div className="bg-white p-6 rounded-2xl border border-slate-200 hover:border-red-100 hover:shadow-lg transition-all group">
                    <Code2 className="h-8 w-8 text-red-600 mb-4 group-hover:scale-110 transition-transform" />
                    <h3 className="text-lg font-bold text-slate-900 mb-2">Blockchain</h3>
                    <p className="text-slate-600 text-sm">Smart contracts and decentralized applications (dApps).</p>
                </div>
                <div className="bg-white p-6 rounded-2xl border border-slate-200 hover:border-red-100 hover:shadow-lg transition-all group">
                    <Database className="h-8 w-8 text-red-600 mb-4 group-hover:scale-110 transition-transform" />
                    <h3 className="text-lg font-bold text-slate-900 mb-2">Data Engineering</h3>
                    <p className="text-slate-600 text-sm">Architecting data lakes and pipelines for AI readiness.</p>
                </div>
            </div>
      </div>

      {/* --- SECTION 2: COMPLIANCE (SECURE - Dark) --- */}
      <div className="bg-slate-900 pt-8 pb-16 md:pt-10 md:pb-24 text-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
             <span className="text-red-500 font-bold tracking-wider uppercase text-sm">Secure</span>
             <h2 className="text-3xl md:text-4xl font-bold mt-2">Regulatory Compliance</h2>
             <p className="text-slate-400 max-w-2xl mx-auto mt-4">
               We don't just build software; we build *compliant* software.
             </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div className="flex flex-col items-center text-center p-8 bg-slate-800 border border-slate-700 rounded-2xl">
               <div className="h-16 w-16 bg-blue-900/50 text-blue-400 rounded-full flex items-center justify-center mb-6 border border-blue-800"><ShieldCheck className="h-8 w-8" /></div>
               <h3 className="text-xl font-bold text-white mb-3">SOC 2 Compliance</h3>
               <p className="text-slate-400">Security controls, monitoring, and audit trails for Type I and II attestation.</p>
            </div>
            <div className="flex flex-col items-center text-center p-8 bg-slate-800 border border-slate-700 rounded-2xl">
               <div className="h-16 w-16 bg-green-900/50 text-green-400 rounded-full flex items-center justify-center mb-6 border border-green-800"><Lock className="h-8 w-8" /></div>
               <h3 className="text-xl font-bold text-white mb-3">HIPAA Compliance</h3>
               <p className="text-slate-400">Protect PHI with end-to-end encryption and strict access controls.</p>
            </div>
          </div>
        </div>
      </div>

      {/* --- SECTION 3: STRATEGY (LEAD) --- */}
      <div className="max-w-7xl mx-auto px-4 pt-8 pb-16 md:pt-10 md:pb-24">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <span className="text-red-600 font-bold tracking-wider uppercase text-sm">Lead</span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mt-2 mb-6">Fractional Leadership</h2>
            <p className="text-slate-600 text-lg mb-8">
              We provide high-level technical leadership on demand.
            </p>
            <div className="space-y-6">
              <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm flex gap-4">
                <Briefcase className="h-6 w-6 text-red-600 shrink-0 mt-1" />
                <div><h4 className="font-bold text-lg text-slate-900">Rent a CTO</h4><p className="text-slate-600 text-sm mt-1">Interim technical leadership for startups and scale-ups.</p></div>
              </div>
              <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm flex gap-4">
                <Cpu className="h-6 w-6 text-red-600 shrink-0 mt-1" />
                <div><h4 className="font-bold text-lg text-slate-900">Solutions Architect On-Demand</h4><p className="text-slate-600 text-sm mt-1">Design robust, scalable systems before writing code.</p></div>
              </div>
            </div>
          </div>
          <div className="bg-slate-100 rounded-3xl p-8 border border-slate-200">
             <div className="grid grid-cols-1 gap-8">
                <div className="text-center"><div className="text-4xl font-bold text-slate-900 mb-2">100+</div><div className="text-sm text-slate-500 uppercase tracking-widest">Products Launched</div></div>
                <div className="w-full h-px bg-slate-300"></div>
                <div className="text-center"><div className="text-4xl font-bold text-slate-900 mb-2">5x</div><div className="text-sm text-slate-500 uppercase tracking-widest">Faster Time to Market</div></div>
             </div>
          </div>
        </div>
      </div>

      <ServicesGrid />

    </div>
  );
}